import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import CreateWABAError from 'components/commonComponents/createWABALoader/CreateWABAError';
import CreateWABALoader from 'components/commonComponents/createWABALoader';
// import WhatsAppConnectionModal from '../../channels/page/whatsapp/components/WhatsAppConnectionModal';
import WhatAppBusiness from '../componenst/WhatAppBusiness';

import { createWABA, retryCreateWABA } from 'api/apiClients/meta/whatsapp';
import { broadcastPerformance } from 'api/apiClients/broadcast';
import { creditsApi, currentUserDetails } from 'api/Api';
import { setupApi, getBrandModeDetails } from '../../accounts/api/Api';
import { fetchBusinessInsights, whatsappBusinessNumbers, fetchNeedsAttentionAlerts } from '../api/Api';
import { configId, solutionID } from 'api/ApiConfig';
import { capitalizeFirstLetter } from 'helper/capitalizeFirstLetter';

import { useAspDispatch, useAspSelector } from 'test/jest-redux-hooks';
import {
  setBrandUserRoles,
  updateCurrentBrand,
  updateCurrentUser,
} from 'reduxToolkit/appSlice';

import HomeBanner from '../componenst/HomeBanner';
import HomeHeader from '../componenst/HomeHeader';
import BusinessDetailsCard from '../componenst/BusinessDetailsCard';
import GettingStartedCard from '../componenst/GettingStartedCard';
import RecentBroadcastsCard from '../componenst/RecentBroadcastsCard';
import WeeklySummaryCard from '../componenst/WeeklySummaryCard';
import NeedsAttentionCard from '../componenst/NeedsAttentionCard';
import AnnouncementsCard from '../componenst/AnnouncementsCard';
import PricingPlanModal from '../../accounts/components/PricingPlanModal';

const CAROUSEL_DELAY_MS = 7000;

const QUALITY_MAP = {
  GREEN: { label: 'High', color: '#0ACD95' },
  YELLOW: { label: 'Medium', color: '#F6A723' },
  RED: { label: 'Low', color: '#F96056' },
  UNKNOWN: { label: 'Unknown', color: '#98A2B3' },
};

/**
 * Home Component
 *
 * Main dashboard page displaying:
 * - WhatsApp Business connection status and notifications
 * - Business details (account, plan, credits, quality, limits)
 * - Getting started checklist
 * - Recent broadcasts and weekly summary analytics
 * - Attention items and announcements
 */
const Home = () => {
  const navigate = useNavigate();
  const dispatch = useAspDispatch();

  const currentBrand = useAspSelector((state) => state?.app?.currentBrand);
  const currentAccount = useAspSelector((state) => state?.app?.currentAccount);
  const currentUser = useAspSelector((state) => state?.app?.currentUser);

  const [sessionData, setSessionData] = useState(null);
  const [data, setData] = useState(null);
  const [isRes, setIsRes] = useState(false);
  const [isResError, setIsResError] = useState({
    error: false,
    data: '',
  });

  const [setupData, setSetupData] = useState([]);
  const [brandModeDetails, setBrandModeDetails] = useState(null);
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [whatsappBusinessAccountName, setWhatsappBusinessAccountName] =
    useState(null);
  const [wabaStatus, setWabaStatus] = useState(null);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  // const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [connectionType, setConnectionType] = useState('bsp');
  const [messagingLimitData, setMessagingLimitData] = useState(null);

  const [dismissedNotifications, setDismissedNotifications] = useState([]);
  const [activeNotificationIndex, setActiveNotificationIndex] = useState(0);
  const [isBannerVisible, setIsBannerVisible] = useState(true);

  const [broadcastRangeCount, setBroadcastRangeCount] = useState(3);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [recentBroadcasts, setRecentBroadcasts] = useState([]);

  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [weeklySummary, setWeeklySummary] = useState({
    newLeads: null,
    marketing: null,
    marketingLite: null,
    utility: null,
    authentication: null,
  });

  const [alertsLoading, setAlertsLoading] = useState(false);
  const [attentionAlerts, setAttentionAlerts] = useState([]);

  const [planDetails, setPlanDetails] = useState(null);
  const [brandCredits, setBrandCredits] = useState(null);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);

  const isWhatsAppConnected = currentBrand?.is_whatsapp_connected;

  const fetchWhatsappNumbers = useCallback(async () => {
    if (!currentBrand?.brand_id) return;

    setLoadingNumbers(true);
    try {
      const res = await whatsappBusinessNumbers(currentBrand.brand_id, {
        brand_id: currentBrand.brand_id,
        call_meta: 'false',
      });

      if (res?.data?.wapp_business_num?.length > 0) {
        setWhatsappNumbers(res.data.wapp_business_num);
        setSelectedNumber(res.data.wapp_business_num[0]);
      }

      // Store messaging limit data, business account name, and WABA status
      if (res?.data) {
        setMessagingLimitData({
          limit: res.data.messaging_limit,
          tierName: res.data.messaging_limit_tier_name,
          limitReached: res.data.message_limit_reached,
          remaining: res.data.remaining_messages,
        });
        // Store business account name from root of API response
        setWhatsappBusinessAccountName(res.data.whatsapp_business_account_name);
        // Store WABA status from root of API response
        setWabaStatus(res.data.waba_status);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp numbers:', error);
    } finally {
      setLoadingNumbers(false);
    }
  }, [currentBrand?.brand_id]);

  const getBroadcasts = useCallback(async (brandId, count) => {
    if (!brandId || !count) return;
    setBroadcastLoading(true);
    setRecentBroadcasts([]);
    try {
      const res = await broadcastPerformance(brandId, null, null, count);
      const payload = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRecentBroadcasts(payload);
    } catch (error) {
      console.warn(error);
    } finally {
      setBroadcastLoading(false);
    }
  }, []);

  const handleBusinessInsights = useCallback(
    async (brandId, startEpoch, endEpoch) => {
      try {
        const res = await fetchBusinessInsights(brandId, startEpoch, endEpoch, [
          'new_leads',
        ]);

        const pricingInsights = res?.data?.pricing_insights || [];
        const delivered = {
          marketing: 0,
          marketing_lite: 0,
          utility: 0,
          authentication: 0,
        };

        pricingInsights.forEach((item) => {
          const category = item?.pricing_category?.toLowerCase();
          if (
            !category ||
            !Object.prototype.hasOwnProperty.call(delivered, category)
          ) {
            return;
          }
          delivered[category] += Number(item?.volume || 0);
        });

        setWeeklySummary((prev) => ({
          ...prev,
          newLeads: res?.data?.new_leads_count || 0,
          marketing: delivered.marketing,
          marketingLite: delivered.marketing_lite,
          utility: delivered.utility,
          authentication: delivered.authentication,
        }));
      } catch (error) {
        console.warn('Failed to fetch business insights:', error);
      }
    },
    [],
  );

  const handleNeedsAttentionAlerts = useCallback(async (brandId) => {
    if (!brandId) return;
    setAlertsLoading(true);
    try {
      const res = await fetchNeedsAttentionAlerts(brandId);
      if (res?.status === 200 && res?.data?.alerts) {
        setAttentionAlerts(res.data.alerts);
      }
    } catch (error) {
      console.warn('Failed to fetch needs attention alerts:', error);
      setAttentionAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentBrand?.brand_id) return;

    const brandId = currentBrand.brand_id;

    // Initial data fetch
    setupApi(brandId).then((res) => {
      if (res?.status === 200) {
        setSetupData(res?.data);
      }
    });

    creditsApi(brandId).then((res) => {
      if (res?.status === 200) {
        setPlanDetails(res?.data?.plan_details);
        setBrandCredits(res?.data?.brand_credits);
      }
    });

    fetchWhatsappNumbers();

    // Weekly Summary Insights
    const startDate = moment().subtract(6, 'days').format('YYYY-MM-DD');
    const endDate = moment().format('YYYY-MM-DD');
    const startEpoch = moment(startDate).startOf('day').valueOf() / 1000;
    const endEpoch = moment(endDate).endOf('day').valueOf() / 1000;

    setWeeklyLoading(true);
    handleBusinessInsights(brandId, startEpoch, endEpoch).finally(() =>
      setWeeklyLoading(false),
    );

    // Needs Attention Alerts
    handleNeedsAttentionAlerts(brandId);

    // 5. Brand Mode Details (test mode only)
    if (currentBrand?.brand_mode === 'test') {
      getBrandModeDetails(brandId)
        .then((data) => setBrandModeDetails(data))
        .catch((error) =>
          console.error('Error fetching brand mode details:', error),
        );
    }
  }, [currentBrand?.brand_id, fetchWhatsappNumbers, handleBusinessInsights, handleNeedsAttentionAlerts]);

  useEffect(() => {
    if (!currentBrand?.brand_id) return;
    getBroadcasts(currentBrand.brand_id, broadcastRangeCount);
  }, [currentBrand?.brand_id, broadcastRangeCount, getBroadcasts]);

  useEffect(() => {
    window.addEventListener('message', sessionInfoListener);
    return () => window.removeEventListener('message', sessionInfoListener);
  }, []);

  useEffect(() => {
    if (sessionData && data) {
      const payload = {
        brand_id: currentBrand?.brand_id,
        waba_id: sessionData?.waba_id,
        phone_number_id: sessionData?.phone_number_id,
        code: data?.authResponse?.code,
        connection_type: connectionType === 'bsp' ? 'bsp' : 'meta',
      };
      createWhatsappAccount(payload);
    }
  }, [data, sessionData]);

  const calculateRemainingDays = (createdAt) => {
    if (!createdAt) return 0;
    const createdDate = new Date(createdAt);
    const expiryDate = new Date(createdDate);
    expiryDate.setDate(expiryDate.getDate() + 7);
    const today = new Date();
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const sessionInfoListener = (event) => {
    if (
      event.origin !== 'https://www.facebook.com' &&
      event.origin !== 'https://web.facebook.com'
    ) {
      return;
    }
    try {
      const payload = JSON.parse(event.data);
      if (payload.type === 'WA_EMBEDDED_SIGNUP') {
        if (payload.event === 'FINISH') {
          setSessionData(payload?.data);
        } else if (payload.event === 'CANCEL') {
          const { current_step } = payload.data;
          console.warn('Cancel at', current_step);
        } else if (payload.event === 'ERROR') {
          const { error_message } = payload.data;
          console.error('Error', error_message);
        }
      }
    } catch {
      console.warn('Non JSON Response', event.data);
    }
  };

  const createWhatsappAccount = async (payload) => {
    setIsRes(true);
    try {
      const res = await createWABA(payload);
      if (res?.status === 200) {
        await fetchCurrentUserDetails();
        setIsRes(false);
      } else {
        const errorData = {
          error: true,
          data: res?.response?.data?.error,
        };
        setIsResError(errorData);
      }
    } catch (e) {
      console.warn(e);
    }
  };

  const retryWABACreate = async () => {
    setIsRes(true);
    setIsResError({
      error: false,
      data: '',
    });
    try {
      const res = await retryCreateWABA({
        brand_id: currentBrand?.brand_id,
        connection_type: connectionType === 'bsp' ? 'bsp' : 'meta',
      });
      if (res.status === 200) {
        const newRes = await fetchCurrentUserDetails();
        if (newRes?.status === 200) {
          window.location.reload();
          setIsRes(false);
        }
        await fetchCurrentUserDetails();
        setIsRes(false);
      } else {
        setIsResError({
          error: true,
          data: res?.response?.data?.error,
        });
      }
    } catch (e) {
      console.warn(e);
    }
  };

  const fetchCurrentUserDetails = async () => {
    try {
      const res = await currentUserDetails();
      dispatch(updateCurrentUser(res?.data));
      dispatch(setBrandUserRoles(res?.data));
      const checkBrandExist = res?.data?.active_accounts
        ?.find((a) => a?.account_id === currentAccount?.account_id)
        ?.brands?.filter(
          (b) =>
            b?.brand_id === currentBrand?.brand_id &&
            b?.is_whatsapp_connected !== currentBrand?.is_whatsapp_connected,
        );

      setIsRes(false);
      if (checkBrandExist?.length > 0) {
        dispatch(updateCurrentBrand(checkBrandExist?.[0]));
        dispatch(updateCurrentUser(res?.data));
      } else {
        dispatch(
          updateCurrentBrand(res?.data?.active_accounts?.[0]?.brands?.[0]),
        );
      }
    } catch (error) {
      setIsRes(false);
      console.warn('Error fetching current user details:', error);
    }
  };

  const launchWhatsAppSignup = async () => {
    // Uncomment below to show modal for connection type selection
    // setIsSelectionModalOpen(true);
    
    // Direct BSP connection (bypassing modal)
    const type = 'bsp'; // Default to BSP connection type
    setConnectionType(type);
    await FB.login(
      function (response) {
        if (response.authResponse) {
          setData(response);
        } else {
          console.warn('User cancelled login or did not fully authorize.');
        }
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          sessionInfoVersion: 3,
          version: 'v3',
          setup: { solutionID: solutionID }, // Always use BSP with solutionID
          features: [
            {
              name: 'marketing_messages_lite',
            },
          ],
        },
      },
    );
  };

  // Uncomment to enable modal-based connection type selection
  // const handleWhatsAppSignup = async (type) => {
  //   setConnectionType(type);
  //   setIsSelectionModalOpen(false);
  //   await FB.login(
  //     function (response) {
  //       if (response.authResponse) {
  //         setData(response);
  //       } else {
  //         console.warn('User cancelled login or did not fully authorize.');
  //       }
  //     },
  //     {
  //       config_id: configId,
  //       response_type: 'code',
  //       override_default_response_type: true,
  //       extras: {
  //         sessionInfoVersion: 3,
  //         version: 'v3',
  //         setup: type === 'bsp' ? { solutionID: solutionID } : {},
  //         features: [
  //           {
  //             name: 'marketing_messages_lite',
  //           },
  //         ],
  //       },
  //     },
  //   );
  // };

  const notifications = useMemo(() => {
    const items = [];

    if (currentBrand?.brand_mode === 'test') {
      const remaining = calculateRemainingDays(brandModeDetails?.created_at);
      if (remaining === 0) {
        items.push({
          id: 'test-expired',
          title: 'Test account expired',
          description: 'Reconnect or switch to a live business account.',
          priority: 'warning',
          dismissible: false,
          actionLabel: 'Reconnect',
          onClick: launchWhatsAppSignup,
        });
      } else if (remaining <= 2) {
        items.push({
          id: 'test-expiring',
          title: 'Test account expiring',
          description: `Expires in ${remaining} day${
            remaining === 1 ? '' : 's'
          }.`,
          priority: 'warning',
          dismissible: false,
          actionLabel: 'Extend',
          onClick: launchWhatsAppSignup,
        });
      }
    }

    items.push(
      // {
      //   id: 'announcement-new-templates',
      //   title: 'New announcement',
      //   description:
      //     'Template performance insights are now visible in campaigns.',
      //   priority: 'info',
      //   dismissible: true,
      // },
      // {
      //   id: 'announcement-launch',
      //   title: 'Product announcement',
      //   description: 'Sequence builder has new branching conditions.',
      //   priority: 'info',
      //   dismissible: true,
      // },
    );

    return items.filter((item) => !dismissedNotifications.includes(item.id));
  }, [
    brandModeDetails?.created_at,
    calculateRemainingDays,
    currentBrand?.brand_mode,
    dismissedNotifications,
  ]);

  useEffect(() => {
    if (!isWhatsAppConnected) return;
    if (notifications.length <= 1) return;

    const interval = setInterval(() => {
      setActiveNotificationIndex((prev) =>
        prev + 1 >= notifications.length ? 0 : prev + 1,
      );
    }, CAROUSEL_DELAY_MS);

    return () => clearInterval(interval);
  }, [isWhatsAppConnected, notifications.length]);

  useEffect(() => {
    if (!notifications.length) return;
    if (!isWhatsAppConnected) {
      setActiveNotificationIndex(0);
    } else if (activeNotificationIndex >= notifications.length) {
      setActiveNotificationIndex(0);
    }
  }, [activeNotificationIndex, isWhatsAppConnected, notifications.length]);

  useEffect(() => {
    setIsBannerVisible(true);
  }, [isWhatsAppConnected]);

  const dismissNotification = (id) => {
    setDismissedNotifications((prev) => [...prev, id]);
  };

  const formatNumber = (value) => {
    if (value === 0) return '0';
    if (value === undefined || value === null || value === '') return '--';
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return value;
    return parsed.toLocaleString();
  };

  const formatRate = (numerator, denominator) => {
    const num = Number(numerator);
    const den = Number(denominator);
    if (!den || Number.isNaN(den) || Number.isNaN(num)) return '--';
    return `${Math.round((num / den) * 100)}%`;
  };

  const getPlanInfo = () => {
    if (!planDetails) return null;

    return {
      isTrial: !!planDetails.is_trial,
      daysLeft: planDetails.days_left || 0,
      isExpired: planDetails.days_left <= 0 || planDetails.status === 9,
      planCode: planDetails.plan_code || 'free',
      planName: planDetails.plan_name || 'Free Trial',
    };
  };

  const canUpgrade = () => {
    if (!planDetails) return false;
    const planInfo = getPlanInfo();
    if (!planInfo) return false;
    if (planInfo.planCode === 'magnum') return false; // already top plan

    // account-level role check
    const accountRole = currentAccount?.role;
    const brandRole = currentUser?.active_accounts
      ?.find((acc) => acc.account_id === currentAccount?.account_id)
      ?.brands?.find(
        (brand) => brand.brand_id === currentBrand?.brand_id,
      )?.role;

    const isAccountElevated =
      accountRole === 'Account Owner' || accountRole === 'Account Manager';
    const isBrandElevated = brandRole === 'Brand Manager';

    return isAccountElevated || isBrandElevated;
  };

  const handlePaymentSetup = () => {
    const business_id = setupData?.business_id;
    if (!business_id) return;
    window.open(
      `https://business.facebook.com/billing_hub/accounts/details?business_id=${business_id}&placement=standalone&account_type=whatsapp-business-account`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const steps = useMemo(
    () => [
      {
        id: 1,
        title: 'Create campaign',
        description: 'Launch your first campaign to engage customers.',
        key: 'is_campaign_created',
        action: () => navigate('/user/campaign/all-campaigns'),
      },
      {
        id: 2,
        title: 'Create automation',
        description: 'Build sequences to nurture and convert leads.',
        key: 'is_automation_created',
        action: () => navigate('/user/campaign/all-campaigns'),
      },
      {
        id: 3,
        title: 'Create audience',
        description: 'Import contacts or build targeted segments.',
        key: 'is_audience_created',
        action: () => navigate('/user/crm'),
      },
      {
        id: 4,
        title: 'Collect payment',
        description: 'Enable payment collection through WhatsApp messages.',
        key: 'is_whatsapp_payment_configuration_enabled',
        action: () => navigate('/user/channels/whatsapp/payment-configurations'),
      },
    ],
    [navigate],
  );

  const checklistSteps = steps.map((step) => ({
    ...step,
    completed: !!setupData?.[step.key],
  }));

  const announcementItems = useMemo(
    () => [
      {
        id: 'announcement-whatsapp-payments',
        title: 'Collect payments inside WhatsApp',
        description:
          'Payment Configuration now lets you accept payments right in chat—no redirects required.',
        tag: 'New',
        actionLabel: 'Configure payments',
        action: () =>
          navigate('/user/channels/whatsapp/payment-configurations'),
      },
      {
        id: 'announcement-whatsapp-flows',
        title: 'WhatsApp Flows (form-style journeys)',
        description:
          'Create interactive, form-like experiences without sequences and capture responses directly in WhatsApp.',
        actionLabel: 'Open flows',
        action: () => navigate('/user/channels/whatsapp/flows'),
      },
    ],
    [navigate],
  );

  const selectedQuality =
    QUALITY_MAP[selectedNumber?.quality_rating] || QUALITY_MAP.UNKNOWN;

  const displayName = `${
    currentUser?.first_name
      ? capitalizeFirstLetter(currentUser?.first_name)
      : ''
  }${
    currentUser?.last_name
      ? currentUser?.first_name
        ? ` ${capitalizeFirstLetter(currentUser?.last_name)}`
        : capitalizeFirstLetter(currentUser?.last_name)
      : ''
  }`;

  const bannerMessage = isWhatsAppConnected
    ? notifications[activeNotificationIndex]?.title
    : currentBrand?.brand_mode === 'test'
    ? 'You are in test mode. Connect your WhatsApp Business Account to go live.'
    : 'Your WhatsApp business account was disconnected';

  const bannerActionLabel = isWhatsAppConnected
    ? notifications[activeNotificationIndex]?.actionLabel
    : currentAccount?.role !== 'Campaigns Manager'
    ? 'Connect'
    : null;

  const bannerDismissible = isWhatsAppConnected
    ? notifications[activeNotificationIndex]?.dismissible
    : true;

  const showBanner = isWhatsAppConnected
    ? notifications.length > 0
    : isBannerVisible;

  const handlePreviousNotification = () => {
    if (notifications.length <= 1) return;
    setActiveNotificationIndex((prev) =>
      prev === 0 ? notifications.length - 1 : prev - 1,
    );
  };

  const handleNextNotification = () => {
    if (notifications.length <= 1) return;
    setActiveNotificationIndex((prev) =>
      prev + 1 >= notifications.length ? 0 : prev + 1,
    );
  };

  const handleBannerDismiss = () => {
    if (!isWhatsAppConnected) {
      setIsBannerVisible(false);
      return;
    }
    dismissNotification(notifications[activeNotificationIndex]?.id);
  };

  const visibleBroadcasts = useMemo(
    () =>
      broadcastRangeCount
        ? recentBroadcasts.slice(0, broadcastRangeCount)
        : recentBroadcasts,
    [recentBroadcasts, broadcastRangeCount],
  );

  return (
    <div
      className="w-full min-h-screen home-bg"
      style={{
        backgroundColor: 'var(--surface-muted)',
      }}
    >
      <div
        className={`w-full relative ${
          isRes ? 'opacity-[16%] blur-sm' : 'opacity-100 pt-[10px]'
        }`}
      >
        <HomeBanner
          show={showBanner}
          message={bannerMessage}
          actionLabel={bannerActionLabel}
          onAction={launchWhatsAppSignup}
          dismissible={bannerDismissible}
          onDismiss={handleBannerDismiss}
          showNav={isWhatsAppConnected && notifications.length > 1}
          onPrev={handlePreviousNotification}
          onNext={handleNextNotification}
        />

        <div className="w-[92%] mx-auto py-8 flex flex-col gap-6">
          <HomeHeader
            displayName={displayName}
            brandName={currentBrand?.brand_name || 'your brand'}
            showUpgrade={canUpgrade()}
            onUpgrade={() => {
              if (canUpgrade()) setIsPricingModalOpen(true);
            }}
          />

          <div className="grid grid-cols-12 gap-6 items-stretch">
            <div className="col-span-12 lg:col-span-8 h-full">
              <WhatAppBusiness
                setupData={setupData}
                launchWhatsAppSignup={launchWhatsAppSignup}
              />
            </div>
            <BusinessDetailsCard
              whatsappBusinessAccountName={whatsappBusinessAccountName}
              wabaStatus={wabaStatus}
              selectedNumber={selectedNumber}
              whatsappNumbers={whatsappNumbers}
              onSelectNumber={setSelectedNumber}
              loadingNumbers={loadingNumbers}
              planDetails={planDetails}
              brandCredits={brandCredits}
              messagingLimitData={messagingLimitData}
              selectedQuality={selectedQuality}
              isTestAccount={currentBrand?.brand_mode === 'test'}
              brandModeDetails={brandModeDetails}
            />
          </div>

          <GettingStartedCard steps={checklistSteps} />

          <div className="grid grid-cols-12 gap-6">
            <RecentBroadcastsCard
              broadcasts={visibleBroadcasts}
              loading={broadcastLoading}
              rangeCount={broadcastRangeCount}
              onRangeChange={setBroadcastRangeCount}
              formatRate={formatRate}
            />
            <WeeklySummaryCard
              summary={weeklySummary}
              loading={weeklyLoading}
              formatNumber={formatNumber}
            />
          </div>

          <div className="grid grid-cols-12 gap-6 pb-6">
            <NeedsAttentionCard items={attentionAlerts} loading={alertsLoading} />
            <AnnouncementsCard items={announcementItems} />
          </div>
        </div>
      </div>

      <PricingPlanModal
        open={isPricingModalOpen}
        setOpen={setIsPricingModalOpen}
        brandId={currentBrand?.brand_id}
        brandsData={currentBrand}
        currentAccount={currentAccount}
        onSuccess={() => {
          currentUserDetails().then((res) => {
            if (res?.status === 200) {
              dispatch(updateCurrentUser(res?.data?.data));
            }
          });
          if (currentBrand?.brand_id) {
            creditsApi(currentBrand.brand_id).then((res) => {
              if (res?.status === 200) {
                setPlanDetails(res?.data?.plan_details);
                setBrandCredits(res?.data?.brand_credits);
              }
            });
          }
        }}
      />

      {isRes ? (
        <div className="w-full h-full absolute top-0 left-0 flex flex-col items-center justify-center">
          {isResError?.error ? (
            <>
              <CreateWABAError width={'11vw'} height={'6vw'} />
              <p
                className={`font-large weight-semibold mt-6 ${
                  ['payment', 'two-factor'].includes(isResError?.data)
                    ? 'text-[var(--primary)]'
                    : 'text-[#F96056]'
                }`}
              >
                {isResError?.data === 'payment'
                  ? 'Please configure the payment method and click "continue".'
                  : isResError?.data === 'two-factor'
                  ? 'Enable your 2-factor authentication in your WhatsApp manager and click continue.'
                  : 'Unexpected error happen, please retry!'}
              </p>
              <button
                className={`font-md weight-medium text-[#F96056] font-normal weight-bold px-[0.85vw] py-[0.4vw] rounded-[0.6vw] mt-[0.8vw] ${
                  ['payment', 'two-factor'].includes(isResError?.data)
                    ? 'bg-[var(--primary)] text-white'
                    : 'bg-red-500 text-white'
                }`}
                onClick={() => {
                  retryWABACreate();
                }}
              >
                {['payment', 'two-factor'].includes(isResError?.data)
                  ? 'Continue'
                  : 'Retry'}
              </button>
            </>
          ) : (
            <>
              <CreateWABALoader width={'11vw'} height={'6vw'} />
              <p className="font-large weight-semibold bg-gradient-to-r from-[#0ACD95] to-[#6940F2] bg-clip-text text-transparent">
                Hold on, we&apos;re setting up things for you.
              </p>
            </>
          )}
        </div>
      ) : null}

      {/* Uncomment to enable modal-based connection type selection */}
      {/* <WhatsAppConnectionModal
        open={isSelectionModalOpen}
        onCancel={() => setIsSelectionModalOpen(false)}
        onSelect={handleWhatsAppSignup}
      /> */}
    </div>
  );
};

export default Home;
